import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { formatChartSubtitle, formatChartTitle } from "../utils";
import { MetricInfoTooltipContent } from "./charts/MetricInfoTooltip";

interface DashboardCardProps {
  title?: string;
  children: React.ReactNode;
  height?: string | number;
  subtitle?: string;
  tooltip?: string;
  /**
   * When set, the title ℹ️ shows the rich metric tooltip (plain-English
   * definition, general ranges and source) for this metricId — matching the
   * stat-card tooltips. Falls back to plain `tooltip`/`subtitle` text when omitted.
   */
  metricKey?: string;
  details?: string;
  detailsContent?: React.ReactNode;
  titleComponent?: React.ReactNode;
  expandable?: boolean;
  expandedContent?: React.ReactNode;
}

export const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  children,
  subtitle,
  tooltip,
  metricKey,
  details,
  detailsContent,
  height = "100%",
  titleComponent,
  expandable = true,
  expandedContent,
}) => {
  const theme = useTheme();
  const [isInfoOpen, setIsInfoOpen] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const formattedTitle = formatChartTitle(title);
  const formattedSubtitle = formatChartSubtitle(subtitle);
  const tooltipText =
    tooltip ||
    formattedSubtitle ||
    (title
      ? `Use this chart to understand ${title.toLowerCase()} and compare patterns across the selected timeframe or groups.`
      : "");
  const detailsText =
    details ||
    (title
      ? `${
          formattedSubtitle ? `${formattedSubtitle} ` : ""
        }Use this view to spot changes over time, compare segments, and identify outliers worth a closer look.`
      : formattedSubtitle || "");
  const hasDetails = Boolean(detailsContent || detailsText);
  // When a metricKey is supplied, the ℹ️ shows the rich definition+ranges+source
  // tooltip; otherwise it falls back to the plain text.
  const tooltipNode: React.ReactNode = metricKey ? (
    <MetricInfoTooltipContent metricKey={metricKey} fallbackText={tooltipText} />
  ) : (
    tooltipText
  );
  const hasTooltip = Boolean(metricKey || tooltipText);
  const showHeader = Boolean(title || titleComponent || hasTooltip || hasDetails || expandable);

  return (
    <Card
      sx={{
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "none",
        height: height,
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: 3,
        }}
      >
        {showHeader && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              mb: title || subtitle ? 2 : 0,
              width: "100%",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, width: "100%", mb: { xs: 2, md: 0 } }}>
              {formattedTitle && (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">{formattedTitle}</Typography>
                    {hasTooltip ? (
                      <Tooltip title={tooltipNode} arrow>
                        <IconButton
                          size="small"
                          sx={{
                            p: 0.5,
                            color: "text.secondary",
                            "&:hover": {
                              color: "text.primary",
                            },
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                  {formattedSubtitle && (
                    <Typography variant="caption" color="text.secondary">
                      {formattedSubtitle}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-end"
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              {!title && hasTooltip ? (
                <Tooltip title={tooltipNode} arrow>
                  <IconButton
                    size="small"
                    sx={{
                      p: 0.5,
                      color: "text.secondary",
                      "&:hover": {
                        color: "text.primary",
                      },
                    }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {hasDetails ? (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setIsInfoOpen(true)}
                >
                  More info
                </Button>
              ) : null}
              {expandable && (
                <Tooltip title="Expand chart" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setIsExpanded(true)}
                    sx={{
                      p: 0.5,
                      color: "text.secondary",
                      "&:hover": { color: "text.primary" },
                    }}
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {titleComponent}
            </Stack>
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            height: "100%",
          }}
        >
          {children}
        </Box>
        {hasDetails ? (
          <Dialog
            open={isInfoOpen}
            onClose={() => setIsInfoOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>{formattedTitle || "Chart details"}</DialogTitle>
            <DialogContent>
              {detailsContent ? (
                detailsContent
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-line" }}
                >
                  {detailsText}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsInfoOpen(false)} variant="contained">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        ) : null}
        {expandable && (
          <Dialog
            open={isExpanded}
            onClose={() => setIsExpanded(false)}
            fullWidth
            maxWidth={false}
            PaperProps={{
              sx: {
                width: "95vw",
                height: "90vh",
                maxWidth: "95vw",
                maxHeight: "90vh",
              },
            }}
          >
            <DialogTitle>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  {formattedTitle && (
                    <Typography variant="h6">{formattedTitle}</Typography>
                  )}
                  {formattedSubtitle && (
                    <Typography variant="body2" color="text.secondary">
                      {formattedSubtitle}
                    </Typography>
                  )}
                </Box>
                <Button
                  onClick={() => setIsExpanded(false)}
                  variant="outlined"
                  size="small"
                >
                  Close
                </Button>
              </Stack>
            </DialogTitle>
            <DialogContent
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                overflow: "hidden",
                p: 3,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  "& > *": { height: "100% !important" },
                  "& .MuiChartsWrapper-root": { height: "100% !important" },
                }}
              >
                {expandedContent || children}
              </Box>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
};
